import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { 
  CheckCircle, XCircle, Loader2, Play, Package, Database, AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const ServiceAssetTestsSection = () => {
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);

  const runTests = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/health/tests/service-asset-flow`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      
      const data = await response.json();
      setResults(data);
      
      if (data.summary?.passRate === 100) {
        toast.success(`Service/Asset: All ${data.summary.total} tests passed!`);
      } else {
        toast.warning(`Service/Asset: ${data.summary.passed}/${data.summary.total} tests passed`);
      }
    } catch (error) {
      toast.error('Failed to run service/asset tests');
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'passed': return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'failed': return <XCircle className="w-5 h-5 text-red-500" />;
      case 'skipped': return <AlertTriangle className="w-5 h-5 text-yellow-500" />;
      default: return <Loader2 className="w-5 h-5 text-gray-400 animate-spin" />;
    }
  };

  return (
    <Card className="border-orange-200 bg-orange-50/30">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Package className="w-5 h-5 text-orange-600" />
            <CardTitle className="text-lg text-orange-900">
              Service & Asset Management (PostgreSQL)
            </CardTitle>
          </div>
          <Button
            onClick={runTests}
            disabled={loading}
            size="sm"
            className="bg-orange-600 hover:bg-orange-700"
          >
            {loading ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Running...</>
            ) : (
              <><Play className="w-4 h-4 mr-2" />Run Tests</>
            )}
          </Button>
        </div>
        <p className="text-sm text-orange-700 mt-1">
          Tests: Create/Update/Delete services → Create/List assets → Link asset to service
        </p>
      </CardHeader>
      
      <CardContent>
        {results ? (
          <div className="space-y-4">
            <div className={`p-3 rounded-lg ${
              results.summary?.passRate === 100 
                ? 'bg-green-100 border border-green-300' 
                : 'bg-yellow-100 border border-yellow-300'
            }`}>
              <div className="flex items-center justify-between">
                <span className="font-medium">
                  {results.summary?.passed}/{results.summary?.total} tests passed
                </span>
                <span className={`text-lg font-bold ${
                  results.summary?.passRate === 100 ? 'text-green-700' : 'text-yellow-700'
                }`}>
                  {results.summary?.passRate}%
                </span>
              </div>
            </div>

            <div className="space-y-2">
              {results.tests?.map((test, index) => (
                <div 
                  key={index}
                  className={`p-3 rounded-lg border ${
                    test.status === 'passed' ? 'bg-green-50 border-green-200' 
                    : test.status === 'failed' ? 'bg-red-50 border-red-200'
                    : 'bg-yellow-50 border-yellow-200'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    {getStatusIcon(test.status)}
                    <div className="flex-1">
                      <span className="font-medium text-sm">{test.name}</span>
                      <p className="text-xs text-gray-600 mt-1">{test.message}</p>
                      {test.endpoint && (
                        <p className="text-xs text-gray-500 mt-1 font-mono">{test.endpoint}</p>
                      )}
                      {test.database && (
                        <span className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded">
                          <Database className="w-3 h-3" />{test.database}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {results.cleanup && (
              <div className={`p-2 rounded text-xs ${
                results.cleanup.status === 'success' ? 'bg-gray-100 text-gray-600' : 'bg-red-100 text-red-600'
              }`}>
                Cleanup: {results.cleanup.status}
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            <Package className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p>Click "Run Tests" to verify service/asset management</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ServiceAssetTestsSection;
